import React, { useContext, useEffect, useMemo, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { useWindowSize } from "@reach/window-size";
import dayjs from "dayjs";
import { getCountry } from "country-currency-map";
import {
  BoxBody,
  EmptyWrapper,
  EmptyWrapperTitle,
  EmptyWrapperSubTitle,
  MobileBody,
  TitleParagraph,
  Wrapper,
  BreakDown,
  InfoTrigger,
} from "./style";
import CustomTable from "../../components/common/CustomTable";
import { tableColumns, allCollectionsColumn } from "./utils";
import TitleFilter from "../../components/balances/TitleFilter";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  collectionBreakDown,
  defaultExchangeRate,
  filteredPayoutRequests,
  settlementFeeByCountry,
  settlementsBalance,
  settlementsInfo,
  settlementsPaid,
  settlementsPendingBalance,
} from "./settlementsSlice";
import { convertArrayToObject } from "../../utils/format";
import Loader from "../../components/common/Loader";
import {
  formatCurrencyAmount,
  formatCurrencyCodeAmount,
} from "../../utils/currency";
import CustomModal from "../../components/common/CustomModal";
import PayoutModal from "../../components/balances/PayoutModal";
import { NetworkErrorAlertContext } from "../../context/NetworkErrorAlert";
import BalanceListCard from "../../components/balances/BalanceListCard";
import PreviewPayout from "../../components/balances/PreviewPayout";
import PayoutOTP from "../../components/balances/PayoutOTP";
import { ReactComponent as EmptyTableIcon } from "../../components/balances/assets/emptyTable.svg";
import {
  getallCollectionsBreakdown,
  transferBalance,
} from "./settlementsAPI";
import { useRequestPayout } from "../../app/queries/settlements";
import { formatLongString } from "../../utils/formatString";
import DrawerContainer from "../../components/common/Drawer/Drawer";
import TransactionDetails from "./TransactionDetails";
import CustomerDetails from "./CustomerDetails";
import { SuccessAlertContext } from "../../context/SuccessAlertContext";
import { selectUserRoleAndPermissions } from "../Settings/Team/redux/slice";
import { encryptPayloadWithMerchantKeyJIT } from "../../utils/encryptionAlgorithm";
import { merchantBusinessKeys } from "../Settings/settingsSlice";
import CustomMobileBalanceTable from "./components/CustomMobileBalanceTable";
import CollectionSummaryCard from "./components/CollectionsSummaryCard";
import { ReactComponent as QuestionMark } from "../../assets/icons/questionMark.svg";
import InfoModal from "./components/InfoModal";
import ExportByFilterModal from "../../components/common/ExportByFilterModal";
import { allWallets } from "../Wallets/redux/slice";
import PayoutModalV2 from "../../components/balances/PayoutModal/PayoutModalV2";
import { STATIC_MERCHANT_EMAILS } from "../../utils/const";
import api from "../../app/api";
import { canUserExport, convertToDateOnly } from "../../utils/helpers";
import { getErrorMessage } from "../Wallets/utils";
import { useCollectionStatementExport } from "../../app/queries/collections";
import { fileFormatApiMap } from "../../components/common/ExportByFilterModal/constants";

interface Props {
  title: string;
  subtitle: string;
}

const EmptyBalanceDescription = ({ title, subtitle }: Props) => {
  return (
    <EmptyWrapper>
      <EmptyWrapperTitle>{title}</EmptyWrapperTitle>
      <EmptyWrapperSubTitle>{subtitle}</EmptyWrapperSubTitle>
    </EmptyWrapper>
  );
};

const Balances = () => {
  const posthog = usePostHog();
  const { width } = useWindowSize();
  const successAlertContext = useContext(SuccessAlertContext);
  const { role, permissions } = useAppSelector(selectUserRoleAndPermissions);
  const { mutate: initiateCollectionStatementExport, isLoading: collectionExportIsLoading } =
    useCollectionStatementExport();

  const hasPayoutPermission = permissions?.["collections"]?.includes(
    "CREATE_COLLECTION_PAYOUT",
  );

  const { onShowAlert: onSuccess } = successAlertContext || {};
  const dispatch = useAppDispatch();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [loading, setLoading] = useState(false);
  const { keys } = useAppSelector((state) => state.settings);
  const [settlementsData, setSettlementsData] = useState([]);
  const [search, setSearch] = useState("");
  const [accountSelectOptions, setAccountSelectOptions] = useState([]);
  const [accountMap, setAccountMap] = useState<any>({});
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [showOTP, setShowOTP] = useState(false);
  const [allCollection, setAllCollection] = useState([]);
  const [exportCollectionData, setExportCollectionData] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("9");
  const [filterDate, setFilterDate] = useState([]);
  const [activeToggle, setActiveToggle] = useState("Collections");
  const [transactionDetail, setTransactionDetail] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [collectionsPageNumber, setCollectionsPageNumber] = useState(1);
  const [collectionPageSize, setCollectionPageSize] = useState(10);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [minimumPayoutValue, setMinimumPayoutValue] = useState(null);
  const [paymentType, setPaymentType] = useState("bank");
  const [fee, setFee] = useState(null);
  const [walletId, setWalletId] = useState(null);
  const [openExportFilterModal, setOpenExportFilterModal] =
    useState<boolean>(false);
  const { userId, businessId } = useAppSelector((state) => state.users);
  const { mutateAsync: requestPayoutMutation } = useRequestPayout();

  const { bankAccounts, businessCurrency, business } = useAppSelector(
    (state) => state.settings,
  );
  const {
    status: settlementStatus,
    payoutRequests,
    settlementBalance,
    availableCollectionBalance,
    ledgerCollectionBalance,
    todayCollectionBalance,
    collectionHoldBalance,
  } = useAppSelector((state) => state.settlements);

  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext,
  );

  const { onShowAlert: onShowSuccessAlert } = useContext(
    SuccessAlertContext,
  );
  const email = useAppSelector((state) => state.users?.userObj?.accountUser?.email);

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
      dispatch(settlementsBalance(businessId))
      dispatch(collectionBreakDown(businessId))
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    const fetchWalletId = async () => {
      try {
        const response = await api.get(`wallet/business/${businessId}/wallets`);
        const wallets = response.data?.data;

        setWalletId(wallets?.find((wallet) => wallet.currency === businessCurrency)?.id);

      } catch (error) {
        setWalletId(null);
      }
    };

    if (businessId) {
      fetchWalletId();
    }
  }, [businessId]);

  const getMinimumPayoutValue = async () => {
    try {
      const exValue = await dispatch(
        defaultExchangeRate({
          sourceCurrency: "USD",
          amount: 500,
          destinationCurrency: businessCurrency,
        }),
      ).unwrap();
      setMinimumPayoutValue(exValue?.amount / 100);
    } catch (err) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessCurrency) {
      getMinimumPayoutValue();
    }
  }, [businessCurrency, userId]);

  const onRequestClick = () => {
    if (hasPayoutPermission || role === "OWNER") {
      if (business && business?.approved === false) {
        onShowErrorAlert(Error, "Business awaiting approval.");
      } else if (bankAccounts.length < 1) {
        onShowErrorAlert(Error, "Please add a bank account");

        posthog?.capture("request_payout_b2b", {
          status: "failure",
          failure_reason: "No bank account",
          currency: businessCurrency,
        });
      } else {
        setShowModal(true);
        posthog?.capture("request_payout_b2b", {
          status: "success",
          currency: businessCurrency,
        });
      }
    } else {
      onShowErrorAlert("", "Access is denied");
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const getQueryFields = () => {
    const queryFields: any = {};
    if (filterDate.length && filterDate[0].length > 0) {
      const [startDay, endDay] = filterDate;
      queryFields.startDay = startDay;
      queryFields.endDay = endDay;
    }
    if (selectedStatus !== "9") {
      queryFields.payoutStatus = Number(selectedStatus);
    }
    return queryFields;
  };

  const onPageChange = (page: number, size: number) => {
    setPageNumber(page);
    setPageSize(size);
  };

  const onCollectionsPageChange = (page: number, size: number) => {
    setCollectionsPageNumber(page);
    setCollectionPageSize(size);
  };

  const onStatusChange = (value) => {
    setSelectedStatus(value);
    onPageChange(1, pageSize);
  };

  const onDateChange = (date, dateString) => {
    setFilterDate(dateString);
    onPageChange(1, pageSize);
  };

  const getFilteredData = async () => {
    const filterPayload = getQueryFields();
    try {
      await dispatch(
        filteredPayoutRequests({
          filter: {
            businessId,
            pageNumber,
            walletId,
            ...filterPayload,
          },
        }),
      );
    } catch (err) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId && walletId) {
      getFilteredData();
    }
  }, [businessId, walletId, pageNumber]);

  const getFeeType = (currency: string) => {
    const merchantData = accountSelectOptions
      ? accountSelectOptions.find(
        (account) => account.value === requestData?.accounts,
      )
      : {};
    const country =
      merchantData?.country?.length > 3
        ? getCountry(merchantData?.country)?.abbreviation
        : merchantData?.country;

    if (currency !== "NGN" && country !== "NG") {
      return "bankTransfer";
    } else {
      return "merchantBankTransferFee";
    }
  };

  const getRequestFee = async () => {
    try {
      setLoading(true);
      const countryCode: string =
        business.country.length > 3
          ? getCountry(business.country)?.abbreviation
          : business.country;

      const countryFee = await dispatch(
        settlementFeeByCountry({
          countryCode,
          feeType: getFeeType(businessCurrency),
        }),
      ).unwrap();
      if (countryFee) {
        setFee(countryFee.fees);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const onPreview = () => {
    setShowModal(false);
    setShowPreview(true);
  };

  const onRequestPayout = async (type) => {
    try {
      setLoading(true);
      const requestPayload = {
        walletId: walletId,
        payoutAmount: requestData?.amount,
        selectedAccount: requestData?.accounts,
        businessId,
        fee: fee || null,
        bankAccount: requestData?.accounts?.value,
      };


      let bankPayload: { message: string } | undefined;
      let effectivePublicKey = keys?.data?.publicKey;

      if (type === "bank") {
        if (!businessId) {
          throw new Error("Business information is not available. Please try again.");
        }
        const { encryptedMessage, publicKey } = await encryptPayloadWithMerchantKeyJIT(
          businessId,
          requestPayload,
        );
        bankPayload = { message: encryptedMessage };
        effectivePublicKey = publicKey || effectivePublicKey;
      }

      const walletPayload = { amount: requestData.amount, wallet: walletId };

      if (type === "bank" && bankPayload) {
        await requestPayoutMutation({
          payload: bankPayload,
          token: effectivePublicKey,
        });
        posthog?.capture("otp_payout_b2b", {
          status: "success",
          currency: businessCurrency,
        });
        onSuccess("Bank payout request sent successfully.");
      } else if (type === "wallet") {
        await transferBalance(walletPayload, effectivePublicKey);
        await Promise.all([
          dispatch(allWallets(businessId)),
          dispatch(settlementsBalance({ businessId, businessCurrency })),
          dispatch(settlementsPendingBalance({ businessId })),
          dispatch(settlementsPaid({ businessId })),
        ]);

        posthog?.capture("balance_transfer_b2b", {
          status: "success",
          currency: businessCurrency,
        });
        onSuccess("Wallet balance transferred successfully.");
      }
    } catch (err) {
      const theErrorMessage =
        err?.response?.data?.message || "The request failed.";
      onShowErrorAlert(err, theErrorMessage);
      posthog?.capture(
        type === "bank" ? "otp_payout_b2b" : "balance_transfer_b2b",
        {
          status: "failure",
          failure_reason: theErrorMessage,
          currency: businessCurrency,
        },
      );
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (businessId && userId) {
      dispatch(
        settlementsBalance({
          businessId,
          businessCurrency,
        }),
      );
      dispatch(settlementsPendingBalance({ businessId }));
      dispatch(settlementsPaid({ businessId }));
    }
  }, [businessId, userId]);

  const onFilterButtonClick = () => {
    if (activeToggle === "Payout") {
      getFilteredData();
    } else if (activeToggle === "Collections") {
      getAllCollection();
    }
  };

  const onResetButtonClick = async () => {
    setIsFiltering(true);
    setFilterDate([]);
    await dispatch(
      filteredPayoutRequests({
        filter: {
          businessId,
          walletId,
          status: selectedStatus,
        },
      }),
    );

    setIsFiltering(false);
  };

  useEffect(() => {
    const mercAccounts = bankAccounts?.map((acc) => ({
      value: acc.id,
      label: `${acc.accountNumber} - ${acc.bankName}`,
      accName: acc.accountName,
      accNumber: acc.accountNumber,
      bankName: acc.bankName,
      country: acc.country,
      primary: acc.primary,
    }));
    const mercAccountsMapping = bankAccounts?.map((acc) => ({
      accNo: acc.accountNumber,
      accBank: acc.bankName,
      accName: acc.accountName,
      id: acc.id,
    }));

    const mercAccountsMappingToObj = convertArrayToObject(
      mercAccountsMapping,
      "id",
    );
    setAccountMap(mercAccountsMappingToObj);
    setAccountSelectOptions(mercAccounts);

  }, [bankAccounts]);



  useEffect(() => {
    if (walletId) {
      dispatch(settlementsInfo({ walletId }));
    }
  }, [walletId]);

  const { name: businessName } = business;

  const allPayoutData = () => {
    setSettlementsData([]);
    if (payoutRequests?.length && Object.keys(accountMap)?.length) {
      const tableData = payoutRequests.map((payout) => {
        const isWalletPayout = !payout.merchantWithdrawRequest.bankAccount;
        return {
          key: payout.merchantWithdrawRequest.id,
          payoutid: payout.merchantWithdrawRequest.requestId,
          accBank: isWalletPayout
            ? "Wallet"
            : accountMap[payout?.merchantWithdrawRequest?.bankAccount]
              ?.accBank ?? "-",
          accNo: isWalletPayout
            ? "N/A"
            : accountMap[payout?.merchantWithdrawRequest?.bankAccount]?.accNo ??
            "-",
          accName: isWalletPayout
            ? businessName
            : accountMap[payout?.merchantWithdrawRequest?.bankAccount]
              ?.accName ?? "-",
          amount: formatCurrencyAmount(
            payout.merchantWithdrawRequest?.wallet?.currency,
            payout.merchantWithdrawRequest.amount,
          ),
          status: payout.merchantWithdrawRequest.payoutStatus,
          date: dayjs(payout.merchantWithdrawRequest.createdAt).format(
            "DD.MM.YYYY",
          ),
        };
      });

      if (!businessId) {
        return null;
      } else {
        setSettlementsData(tableData);
      }
    }
  };

  const getAllCollection = async () => {
    const startDate = filterDate[0];
    const endDate = filterDate[1];
    const data = {
      business: businessId,
      startDay: startDate,
      endDay: endDate,
      pageNumber: collectionsPageNumber,
      pageSize: collectionPageSize,
    };

    try {
      if (!businessId) {
        return null;
      } else {
        setLoading(true);
        await getallCollectionsBreakdown(data).then((data) => {
          const payInData = data?.transactionSplit.map((item) => ({
            key: item.ref,
            ref: formatLongString(item.transaction.tnxRef, 20),
            transaction_id: item.transaction.tnxRef,
            merchant_amount:
              formatCurrencyAmount(
                item.transaction.sourceCurrency,
                item.transaction.sourceAmount,
              ) || "--",
            payer_amount:
              formatCurrencyAmount(
                item.transaction.currency,
                Number(item.transaction.chargedAmount) / 100,
              ) || "--",
            currency: item.transaction.settledCurrency || "--",
            source: item.settledAmount || "--",
            rate: item.transaction.rate,
            date:
              dayjs(item.transaction.createdAt).format("DD.MM.YYYY - HH:mm") ||
              "--",
            status: item?.transaction?.toReview
              ? "In review"
              : item?.transaction?.status,
            customer: item.transaction.customer,
            settlement_amount:
              formatCurrencyAmount(
                item.transaction.settledCurrency,
                item.transaction.settledAmount,
              ) || "-",
            amount_less: formatCurrencyAmount(
              item.foreignCurrency,
              item.foreignAmount,
            ),
            paymentType: item.transaction.paymentType,
          }));
          setExportCollectionData(data);
          setAllCollection(payInData);
          setLoading(false);
        });
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (businessId !== null) {
      getAllCollection();
    }
  }, [businessId, collectionPageSize, collectionsPageNumber]);

  const handleRowClick = (row: any) => {
    setTransactionDetail(row);
    setOpenDrawer(true);
  };

  const handleCollectionCardClick = (data: any) => {
    setOpenDrawer(true);
  };

  useEffect(() => {
    if (activeToggle === "Payout") {
      allPayoutData();
    } else if (activeToggle === "Collections") {
      getAllCollection();
    }
  }, [payoutRequests, accountMap, activeToggle]);

  const filteredTransaction = allCollection?.filter((item) =>
    Object.values(item).some(
      (val) =>
        typeof val === "string" &&
        val.toLowerCase().includes(search.toLowerCase()),
    ),
  );

  const filteredPayoutTransaction = settlementsData?.filter((item) =>
    Object.values(item).some(
      (val) =>
        typeof val === "string" &&
        val.toLowerCase().includes(search.toLowerCase()),
    ),
  );

  const handleCloseExportByFilterModal = () => {
    setOpenExportFilterModal(!openExportFilterModal);
  };

  const noPermissionToExport = useMemo(() => !canUserExport("collections", permissions), [permissions]);

  const handleGetCollectionBalanceStatement = (data, clearForm) => {
    if (data.startDate > data.endDate) {
      onShowErrorAlert('', 'The Start Date cannot be later than the End Date');
      return;
    }

    if (noPermissionToExport) {
      onShowErrorAlert("", "Access is denied");
      return;
    }

    initiateCollectionStatementExport(
      {
        businessId,
        startDate: convertToDateOnly(data.startDate) ?? "",
        endDate: convertToDateOnly(data.endDate) ?? "",
        format: fileFormatApiMap[data.fileFormat],
      },
      {
        onSuccess: (result) => {
          onShowSuccessAlert(result?.message ?? 'Collection statement sent to your email');
          clearForm();
          setOpenExportFilterModal(false);
        },
        onError: (error: unknown) => {
          onShowErrorAlert('', getErrorMessage(error, 'Unexpected error, please try again'));
        },
      },
    );
  };

  return (
    <Wrapper>
      <Loader
        isLoading={settlementStatus === "loading" || isFiltering || loading || collectionExportIsLoading}
      />
      <TitleParagraph>
        Collections{" "}
        <InfoTrigger onClick={() => setShowInfoModal(!showInfoModal)}>
          <QuestionMark />
        </InfoTrigger>
      </TitleParagraph>

      {showInfoModal && (
        <CustomModal
          isModalVisible={showInfoModal}
          onClose={() => setShowInfoModal(!showInfoModal)}
          width="464px"
          modalTitle="Collections"
          theme="secondary"
        >
          <InfoModal />
        </CustomModal>
      )}

      <BreakDown>
        <CollectionSummaryCard
          theme="primary"
          title="Available balance"
          mainFigure={
            businessCurrency
              ? formatCurrencyCodeAmount(
                businessCurrency,
                Number(availableCollectionBalance || 0),
              )
              : "0.00"
          }
          subline={`Ledger balance: ${businessCurrency
              ? formatCurrencyCodeAmount(
                businessCurrency,
                Number(ledgerCollectionBalance || 0),
              )
              : "0.00"
            }`}
        />

        <CollectionSummaryCard
          theme="secondary"
          title="Total collection today"
          mainFigure={
            businessCurrency
              ? formatCurrencyCodeAmount(
                businessCurrency,
                Number(todayCollectionBalance || 0),
              )
              : "0.00"
          }
        />

        <CollectionSummaryCard
          theme="tertiary"
          title="Rolling reserve wallet"
          mainFigure={
            businessCurrency
              ? formatCurrencyCodeAmount(
                businessCurrency,
                Number(collectionHoldBalance || 0),
              )
              : "0.00"
          }
        />
      </BreakDown>

      <BoxBody>
        <TitleFilter
          search={search}
          setSearch={setSearch}
          showFilter={settlementsData?.length || allCollection?.length}
          handleExportClick={setOpenExportFilterModal}
          onRequestClick={onRequestClick}
          onDateChange={onDateChange}
          onStatusChange={onStatusChange}
          onFilterButtonClick={onFilterButtonClick}
          onResetButtonClick={onResetButtonClick}
          disableRequest={settlementBalance < 0}
          activeToggle={activeToggle}
          setActiveToggle={setActiveToggle}
        />

        {activeToggle === "Collections" && (
          <div>
            {width > 767 ? (
              <CustomTable
                columns={allCollectionsColumn}
                handleRowClick={handleRowClick}
                dataSource={filteredTransaction}
                extraEmptyChildren={
                  <EmptyBalanceDescription
                    title="No collections yet"
                    subtitle="All collection transactions will appear here."
                  />
                }
                icon={<EmptyTableIcon />}
                currentPage={collectionsPageNumber}
                totalItemCount={exportCollectionData?.totalCount}
                onPageChange={onCollectionsPageChange}
                pageSize={collectionPageSize}
              />
            ) : (
              <MobileBody>
                <CustomMobileBalanceTable
                  settlementsData={filteredTransaction}
                  BalanceListCard={BalanceListCard}
                  currentPage={collectionsPageNumber}
                  totalItemCount={exportCollectionData?.totalCount}
                  onPageChange={onCollectionsPageChange}
                  pageSize={collectionPageSize}
                  pageSizeOptions={[10, 20, 50]}
                  transactionType="Collections"
                />
              </MobileBody>
            )}
          </div>
        )}

        {activeToggle === "Payout" && (
          <div>
            {width > 767 ? (
              <CustomTable
                columns={tableColumns}
                extraEmptyChildren={
                  <EmptyBalanceDescription
                    title="No payout yet"
                    subtitle="All payout transactions will appear here."
                  />
                }
                dataSource={filteredPayoutTransaction}
                icon={<EmptyTableIcon />}
                currentPage={pageNumber}
                onPageChange={onPageChange}
                pageSize={pageSize}
              />
            ) : (
              <MobileBody>
                <CustomMobileBalanceTable
                  settlementsData={filteredPayoutTransaction}
                  BalanceListCard={BalanceListCard}
                  currentPage={pageNumber}
                  onPageChange={onPageChange}
                  pageSize={10}
                  pageSizeOptions={[10, 20, 50]}
                  transactionType="Payout"
                />
              </MobileBody>
            )}
          </div>
        )}

        <CustomModal
          isModalVisible={openExportFilterModal}
          onClose={handleCloseExportByFilterModal}
          width="464px"
          noPadding={true}
        >
          <ExportByFilterModal
            exportByFilterFunction={handleGetCollectionBalanceStatement}
            modalTitle="Get collection statement"
            modalSubTitle="Send your statement to your email"
            submitLabel="Send statement"
            noLimit
          />
        </CustomModal>

        <CustomModal
          isModalVisible={showModal}
          onClose={closeModal}
          width="464px"
        >
          {STATIC_MERCHANT_EMAILS.includes(email) ? (
            <PayoutModalV2
              onCloseModal={closeModal}
              accounts={accountSelectOptions}
              setRequestData={setRequestData}
              onPreview={onPreview}
              businessCurrency={businessCurrency}
              minimumPayoutValue={+minimumPayoutValue}
              getRequestFee={getRequestFee}
              getBankId={requestData?.accounts?.value}
              setPaymentType={setPaymentType}
            />
          ) : (
            <PayoutModal
              onCloseModal={closeModal}
              accounts={accountSelectOptions}
              setRequestData={setRequestData}
              onPreview={onPreview}
              businessCurrency={businessCurrency}
              minimumPayoutValue={+minimumPayoutValue}
              getRequestFee={getRequestFee}
              getBankId={requestData?.accounts?.value}
              setPaymentType={setPaymentType}
              userEmail={email}
            />
          )}
        </CustomModal>

        {showPreview && (
          <PreviewPayout
            setShowModal={setShowModal}
            setShowPreview={setShowPreview}
            setShowOTP={setShowOTP}
            accounts={accountSelectOptions}
            requestData={requestData}
            businessCurrency={businessCurrency}
            fee={fee}
            setLoading={setLoading}
            paymentType={paymentType}
          />
        )}

        {showOTP && (
          <PayoutOTP
            setShowOTP={setShowOTP}
            onRequestPayout={() => onRequestPayout(paymentType)}
            setLoading={setLoading}
          />
        )}
      </BoxBody>

      <DrawerContainer
        title="Transaction details"
        onClose={() => setOpenDrawer(false)}
        open={openDrawer}
        placement={"right"}
      >
        <TransactionDetails
          transaction_id={transactionDetail?.transaction_id}
          merchant_amount={transactionDetail?.merchant_amount}
          reference={transactionDetail?.ref}
          merchantAmount={transactionDetail?.merchant_amount}
          payerAmount={transactionDetail?.payer_amount}
          amountLessFess={transactionDetail?.amount_less || "-"}
          currency={transactionDetail?.currency}
          settledAmount={transactionDetail?.settlement_amount || "-"}
          rate={transactionDetail?.rate}
          date={transactionDetail?.date}
        />

        <CustomerDetails
          email={transactionDetail?.customer?.email}
          phone={transactionDetail?.customer?.phone || "-"}
          method={transactionDetail?.paymentType || "-"}
        />
      </DrawerContainer>
    </Wrapper>
  );
};

export default Balances;